import { useState } from 'react'
import { CheckCircle2, Send, XCircle, Download, AlertTriangle, FilePieChart, ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { CenterSpinner } from '@/components/ui/Spinner'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Input, Label, Textarea } from '@/components/ui/Input'
import { MonthlyAiPanel } from '@/components/insights/MonthlyAiPanel'
import { Badge } from '@/components/ui/Badge'
import { useAuth } from '@/lib/auth/AuthProvider'
import {
  useApproveExport,
  useMonthlyExport,
  useRejectExport,
  useSendExport,
  useVatMonthlySummary,
} from '@/lib/api/hooks'
import { useToast } from '@/components/ui/ToastProvider'
import { hasPermission } from '@/lib/permissions'
import { dateTime, moneyCompact } from '@/lib/format'
import { hu } from '@/lib/i18n/hu'

/** Az "előző hónap"-ra alapértelmezett — a könyvelői flow általában az előző hónap zárása. */
function defaultPeriod(): { year: number; month: number } {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 }
}

const MONTH_NAMES_HU = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
]

export function MonthlyClosePage() {
  const { activeCompanyId, activeRole } = useAuth()
  const [{ year, month }, setPeriod] = useState(() => defaultPeriod())
  const toast = useToast()

  function shiftMonth(delta: number) {
    setPeriod((p) => {
      const d = new Date(p.year, p.month - 1 + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() + 1 }
    })
  }
  function jumpToCurrent() {
    setPeriod(defaultPeriod())
  }

  const vat = useVatMonthlySummary(activeCompanyId ?? '', year, month)
  const exp = useMonthlyExport(activeCompanyId ?? '', year, month)
  const approveExport = useApproveExport()
  const rejectExport = useRejectExport()
  const sendExport = useSendExport()

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [sendOpen, setSendOpen] = useState(false)
  const [accountantEmail, setAccountantEmail] = useState('konyvelo@kkv-konyv.hu')

  if (!activeCompanyId) {
    return <p className="text-sm text-slate-500">Nincs aktív cég.</p>
  }
  if (vat.isLoading || exp.isLoading || !vat.data || !exp.data) return <CenterSpinner />

  const v = vat.data
  const e = exp.data
  const canApprove = hasPermission(activeRole, 'export.approve')
  const canSend = hasPermission(activeRole, 'export.send')

  return (
    <div>
      <PageHeader
        title={`Havi zárás · ${year}. ${MONTH_NAMES_HU[month - 1]}`}
        subtitle="A választott havi tételek összefoglalója, ÁFA előkalkuláció és a könyvelői csomag."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                aria-label="Előző hónap"
                className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={jumpToCurrent}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 border-l border-r border-slate-300 dark:border-slate-700"
                title="Vissza az előző hónapra (default)"
              >
                {year}-{String(month).padStart(2, '0')}
              </button>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                aria-label="Következő hónap"
                className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <Badge tone={e.status === 'approved' ? 'ok' : e.status === 'sent' ? 'brand' : 'warn'}>
              {e.status === 'approval_pending'
                ? 'Jóváhagyásra vár'
                : e.status === 'approved'
                  ? 'Jóváhagyva'
                  : e.status === 'sent'
                    ? 'Elküldve a könyvelőnek'
                    : e.status === 'rejected'
                      ? 'Elutasítva'
                      : e.status === 'draft'
                        ? 'Vázlat'
                        : e.status}
            </Badge>
          </div>
        }
      />

      <MonthlyAiPanel year={year} month={month} companyId={activeCompanyId} />

      <Vat65aPanel year={year} month={month} companyId={activeCompanyId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Összesítő</CardTitle>
              <CardSubtitle>Hónap számlái és állapotuk</CardSubtitle>
            </div>
          </CardHeader>
          <ul className="space-y-1.5 text-sm">
            <Line label="Számlák" value={`${e.counts.invoices} tétel`} />
            <Line label="Hiányzó PDF" value={`${e.counts.missingPdf} tétel`} tone={e.counts.missingPdf > 0 ? 'warn' : 'ok'} />
            <Line label="Csak NAV-ban" value={`${e.counts.navOnly} tétel`} tone={e.counts.navOnly > 0 ? 'warn' : 'ok'} />
            <Line label="Eltéréses tételek" value={`${e.counts.discrepancies} tétel`} tone={e.counts.discrepancies > 0 ? 'warn' : 'ok'} />
          </ul>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>ÁFA előkalkuláció</CardTitle>
              <CardSubtitle className="text-amber-700 flex items-start gap-1">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                {hu.warnings.notOfficialVat}
              </CardSubtitle>
            </div>
          </CardHeader>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Row label="Fizetendő ÁFA" value={moneyCompact(v.totals.vatPayable)} />
            <Row label="Levonható (jelölt)" value={moneyCompact(v.totals.vatDeductibleMarked)} />
            <Row label="Nem levonható (jelölt)" value={moneyCompact(v.totals.vatNonDeductibleMarked)} />
            <Row label="Fordított ÁFA" value={moneyCompact(v.totals.reverseChargeMarked)} />
            <Row label="EU beszerzés" value={moneyCompact(v.totals.euAcquisitionMarked)} />
            <Row label="Felülvizsgálandó" value={`${v.needsReview.length} tétel`} tone="warn" />
          </dl>
        </Card>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <div>
            <CardTitle>Hiányzó / vitás tételek</CardTitle>
            <CardSubtitle>
              Javasoljuk lerendezni a könyvelőnek küldés előtt
            </CardSubtitle>
          </div>
        </CardHeader>
        <ul className="space-y-1.5 text-sm">
          {v.needsReview.length === 0 && <li className="text-slate-500">Nincs jelölt tétel.</li>}
          {v.needsReview.map((n) => (
            <li key={n.invoiceId} className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 flex-shrink-0" />
              <div>
                <div className="font-medium">{n.supplierName}</div>
                <div className="text-xs text-slate-600">{n.reason}</div>
              </div>
              <div className="ml-auto num text-sm font-medium">{moneyCompact(n.grossAmountHuf)}</div>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <div>
            <CardTitle>Csomag</CardTitle>
            <CardSubtitle>
              Generálva: {dateTime(e.approvalRequestedAt ?? '')} · v{e.version}
            </CardSubtitle>
          </div>
          {e.packageFile && (
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => toast.show({ tone: 'info', title: 'Mock letöltés', description: e.packageFile?.downloadUrl })}
            >
              ZIP letöltése ({(e.packageFile.sizeBytes / 1_000_000).toFixed(1)} MB)
            </Button>
          )}
        </CardHeader>
        <ul className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Excel összesítő</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> NAV XML-ek</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> PDF számlák</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Hibalista</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> ÁFA előkalkuláció</li>
          <li className="flex items-center gap-2"><FilePieChart className="h-4 w-4 text-brand-600" /> Vezetői összefoglaló</li>
        </ul>
      </Card>

      <div className="flex flex-wrap gap-2 justify-end">
        {canApprove && e.status === 'approval_pending' && (
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(true)} leftIcon={<XCircle className="h-4 w-4" />}>
              Vissza review-ba
            </Button>
            <Button
              loading={approveExport.isPending}
              onClick={async () => {
                await approveExport.mutateAsync({ id: e.id })
                toast.show({ tone: 'success', title: 'Csomag jóváhagyva' })
              }}
              leftIcon={<CheckCircle2 className="h-4 w-4" />}
            >
              Jóváhagy
            </Button>
          </>
        )}
        {canSend && e.status === 'approved' && (
          <Button onClick={() => setSendOpen(true)} leftIcon={<Send className="h-4 w-4" />}>
            Küldés a könyvelőnek
          </Button>
        )}
        {e.status === 'sent' && (
          <Badge tone="ok">
            Elküldve {e.sentAt ? dateTime(e.sentAt) : ''} · {e.sentToEmail}
          </Badge>
        )}
      </div>

      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Csomag visszaküldése review-ba"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>Mégse</Button>
            <Button
              variant="danger"
              disabled={rejectReason.trim().length < 3}
              loading={rejectExport.isPending}
              onClick={async () => {
                await rejectExport.mutateAsync({ id: e.id, reason: rejectReason })
                toast.show({ tone: 'info', title: 'Csomag elutasítva', description: 'Visszakerült review-ba' })
                setRejectOpen(false)
              }}
            >
              Elutasít
            </Button>
          </>
        }
      >
        <Label htmlFor="reject-reason">Indoklás</Label>
        <Textarea
          id="reject-reason"
          value={rejectReason}
          onChange={(ev) => setRejectReason(ev.target.value)}
          rows={3}
          placeholder="Pl. 9 eltéréses tételt előbb le kell rendezni."
        />
      </Modal>

      <Modal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        title="Küldés a könyvelőnek"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSendOpen(false)}>Mégse</Button>
            <Button
              loading={sendExport.isPending}
              onClick={async () => {
                await sendExport.mutateAsync({ id: e.id, to: accountantEmail })
                toast.show({ tone: 'success', title: 'Elküldve', description: accountantEmail })
                setSendOpen(false)
              }}
              leftIcon={<Send className="h-4 w-4" />}
            >
              Küldés
            </Button>
          </>
        }
      >
        <Label htmlFor="acc-email">Könyvelő email címe</Label>
        <Input
          id="acc-email"
          type="email"
          value={accountantEmail}
          onChange={(ev) => setAccountantEmail(ev.target.value)}
        />
        <p className="mt-2 text-xs text-slate-500">
          Cím módosítása auditolt. Default: a Beállítások / Könyvelő szekcióban megadott cím.
        </p>
      </Modal>
    </div>
  )
}

function Line({ label, value, tone = 'ok' }: { label: string; value: string; tone?: 'ok' | 'warn' | 'error' }) {
  const cls = tone === 'warn' ? 'text-amber-700' : tone === 'error' ? 'text-red-700' : 'text-emerald-700'
  return (
    <li className="flex items-center justify-between">
      <span className="text-slate-700">{label}</span>
      <span className={`num font-medium ${cls}`}>{value}</span>
    </li>
  )
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <>
      <dt className="text-slate-600 text-sm">{label}</dt>
      <dd className={`text-right num font-medium text-sm ${tone === 'warn' ? 'text-amber-700' : 'text-slate-900'}`}>
        {value}
      </dd>
    </>
  )
}

function Vat65aPanel({ year, month, companyId }: { year: number; month: number; companyId: string }) {
  const [downloading, setDownloading] = useState<null | 'txt' | 'xml' | 'json'>(null)
  async function download(format: 'txt' | 'xml' | 'json') {
    setDownloading(format)
    try {
      const token = localStorage.getItem('adaptivo.szamla.token')
      const ext = format === 'json' ? '' : '.' + format
      const r = await fetch(
        `/api/v1/exports/vat-65a/${year}/${month}${ext}?companyId=${encodeURIComponent(companyId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vat-65a-draft-${year}-${String(month).padStart(2, '0')}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Letöltés hiba: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setDownloading(null)
    }
  }
  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-3 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
            📑 NAV ÁFA bevallás (65A) — DRAFT
          </div>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5 max-w-prose">
            A havi számlák ÁFA-kategória szerinti aggregátuma, könyvelőnek átadásra. <strong>Ez DRAFT, nem hivatalos bevallás</strong> —
            a könyvelő ellenőrzi és tölti fel az ONYA-ra.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
          <Button size="sm" variant="secondary" onClick={() => download('txt')} loading={downloading === 'txt'}>
            TXT letöltés
          </Button>
          <Button size="sm" variant="secondary" onClick={() => download('xml')} loading={downloading === 'xml'}>
            XML letöltés
          </Button>
          <Button size="sm" onClick={() => download('json')} loading={downloading === 'json'}>
            JSON adat
          </Button>
        </div>
      </div>
    </div>
  )
}
